<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    required: true,
    validator: value => ['won', 'lost'].includes(value),
  },
  item: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:show', 'confirm']);

const store = useStore();
const { t } = useI18n();

const selectedReason = ref(null);
const otherReason = ref('');
const showOtherInput = ref(false);

const currentFunnel = computed(() => store.getters['funnel/getSelectedFunnel']);

const reasons = computed(() => {
  if (!currentFunnel.value?.settings) return [];
  
  if (props.type === 'won') {
    return currentFunnel.value.settings.win_reasons || [];
  } else {
    return currentFunnel.value.settings.loss_reasons || [];
  }
});

const title = computed(() => {
  return props.type === 'won' 
    ? t('KANBAN.MODALS.WON_TITLE') || 'Motivo do ganho'
    : t('KANBAN.MODALS.LOST_TITLE') || 'Motivo da perda';
});

const confirmLabel = computed(() => {
  return props.type === 'won'
    ? t('KANBAN.MODALS.CONFIRM_WON') || 'Confirmar Ganho'
    : t('KANBAN.MODALS.CONFIRM_LOST') || 'Confirmar Perda';
});

const handleClose = () => {
  emit('update:show', false);
  resetForm();
};

const resetForm = () => {
  selectedReason.value = null;
  otherReason.value = '';
  showOtherInput.value = false;
};

const handleConfirm = () => {
  // Allow confirm if no reasons configured
  if (reasons.value.length > 0 && !selectedReason.value && !showOtherInput.value) return;

  const reason = showOtherInput.value ? otherReason.value : (selectedReason.value?.title || '');
  
  emit('confirm', {
    reason,
    reasonId: selectedReason.value?.id,
    type: props.type
  });
  handleClose();
};

watch(() => props.show, (val) => {
  if (val) resetForm();
});

// Auto-select "Other" if reason allows it, or handle selection logic
const selectReason = (reason) => {
  selectedReason.value = reason;
  showOtherInput.value = false; // logic for 'Outros' could be implemented if 'Outros' is a special reason
};

</script>

<template>
  <Modal :show="show" :on-close="handleClose">
    <div class="flex flex-col h-full bg-white dark:bg-slate-900 rounded-lg">
      <div class="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800">
        <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
          {{ title }}
        </h3>
        <button
          @click="handleClose"
          class="text-slate-400 hover:text-slate-500 transition-colors"
        >
          <fluent-icon icon="dismiss" size="24" />
        </button>
      </div>

      <div class="p-6">
        <div v-if="reasons.length === 0" class="text-center text-slate-500 py-4">
          <p v-if="type === 'won'">Nenhum motivo de ganho configurado.</p>
          <p v-else>Nenhum motivo de perda configurado.</p>
          <p class="text-xs mt-2">Você pode prosseguir sem selecionar um motivo.</p>
        </div>

        <div v-else class="space-y-3">
          <div
            v-for="reason in reasons"
            :key="reason.id"
            class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
            :class="selectedReason?.id === reason.id ? 'border-woot-500 bg-woot-50/50 dark:bg-woot-900/20' : 'border-slate-200 dark:border-slate-700'"
            @click="selectReason(reason)"
          >
            <div 
              class="w-4 h-4 rounded-full border flex items-center justify-center shrink-0"
              :class="selectedReason?.id === reason.id ? 'border-woot-500 bg-woot-500' : 'border-slate-300 dark:border-slate-600'"
            >
              <div v-if="selectedReason?.id === reason.id" class="w-1.5 h-1.5 rounded-full bg-white"></div>
            </div>
            <div>
              <p class="text-sm font-medium text-slate-900 dark:text-slate-100">{{ reason.title }}</p>
              <p v-if="reason.description" class="text-xs text-slate-500 dark:text-slate-400">{{ reason.description }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="p-6 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-2 bg-slate-50 dark:bg-slate-800/50 rounded-b-lg">
        <Button
          variant="ghost"
          color="slate"
          @click="handleClose"
        >
          {{ t('KANBAN.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          :color="type === 'won' ? 'green' : 'red'"
          @click="handleConfirm"
          :disabled="reasons.length > 0 && !selectedReason"
        >
          {{ confirmLabel }}
        </Button>
      </div>
    </div>
  </Modal>
</template>
